import { useApp, useUserInfo } from "@next-platform/client-common-core";
import { applicationRuntime, validateApplicationCapabilityKeys } from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { ApplicationIds, CapabilityTargetPlatform } from "@next-platform/packages-ts-types";
import { useEffect, useMemo, useRef } from "react";

import { useStableStringArray } from "@/hooks/useStableStringArray";

/**
 * App settings refreshes can recreate the application definition object even
 * when its content is unchanged. We keep the previous reference for equal
 * serialized payloads so the runtime context effect only fires for meaningful
 * changes, while the business-logic store remains the final safety net.
 */
function useStableApplicationDefinition<T>(value: T): T {
  const serializedValue = JSON.stringify(value);
  const refValue = useRef(value);
  const refSerializedValue = useRef(serializedValue);

  if (refSerializedValue.current !== serializedValue) {
    refValue.current = value;
    refSerializedValue.current = serializedValue;
  }

  return refValue.current;
}

export default function useApplicationRuntimeContextSync() {
  const { appSettings } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const { isAuthLoaded, roles: capabilityRoles, permissions: capabilityPermissions } = useUserInfo();

  const stableRoles = useStableStringArray(capabilityRoles);
  const stablePermissions = useStableStringArray(capabilityPermissions);
  const rawApplicationDefinition = appSettings?.applicationDefinition ?? null;
  const applicationDefinition = useStableApplicationDefinition(rawApplicationDefinition);
  const viewer = useMemo(
    () =>
      isAuthLoaded && applicationId
        ? {
            applicationId: applicationId as ApplicationIds,
            roles: stableRoles,
            permissions: stablePermissions,
          }
        : null,
    [isAuthLoaded, applicationId, stableRoles, stablePermissions],
  );

  useEffect(() => {
    applicationRuntime.setContext({
      applicationDefinition,
      viewer,
      platform: CapabilityTargetPlatform.WEB,
    });

    validateApplicationCapabilityKeys({
      applicationDefinition,
      applicationId,
      platform: CapabilityTargetPlatform.WEB,
    });
  }, [applicationDefinition, applicationId, viewer]);
}
